import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FlashcardDisplayProps } from '../types';
import { Check, X } from 'lucide-react';

const FlashcardDisplay: React.FC<FlashcardDisplayProps> = ({
  card,
  isFlipped,
  onFlip,
  onKnown,
  onUnknown
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);

  // Calculate if content needs scrolling based on length
  const contentNeedsScroll = useMemo(() => {
    const questionLength = card.question.length;
    const answerLength = card.answer.length;
    const maxLength = Math.max(questionLength, answerLength);
    const maxWordCount = Math.max(
      card.question.split(/\s+/).length,
      card.answer.split(/\s+/).length
    );

    return maxWordCount > 15 || maxLength > 100;
  }, [card.question, card.answer]);

  // Font sizes ~15% smaller than before
  const textSizeClass = 'text-xl md:text-2xl lg:text-3xl font-semibold leading-snug';

  const [swipeState, setSwipeState] = useState({
    isActive: false,
    direction: null as 'left' | 'right' | null,
    distance: 0,
    deltaX: 0,
    deltaY: 0,
    startX: 0,
    startY: 0
  });
  const cardRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Detect if we're on desktop (md breakpoint and above)
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  // Check if content is scrollable
  useEffect(() => {
    if (scrollContainerRef.current && contentNeedsScroll) {
      const element = scrollContainerRef.current;
      const isScrollable = element.scrollHeight > element.clientHeight;
      setShowScrollHint(isScrollable);

      // Hide hint after user scrolls
      const handleScroll = () => {
        if (element.scrollTop > 10) {
          setShowScrollHint(false);
        }
      };

      element.addEventListener('scroll', handleScroll);
      return () => element.removeEventListener('scroll', handleScroll);
    }
  }, [contentNeedsScroll, card, isFlipped]);

  const handleFlip = useCallback(() => {
    if (isAnimating) return;

    setIsAnimating(true);
    onFlip();

    setTimeout(() => {
      setIsAnimating(false);
    }, 300);
  }, [isAnimating, onFlip]);

  const handleCardClick = () => {
    handleFlip();
  };

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    if (event.code === 'Space' || event.key === ' ') {
      event.preventDefault();
      handleFlip();
    }

    if (isFlipped && onKnown && onUnknown) {
      if (event.key === 'ArrowRight' || event.key === 'Enter') {
        event.preventDefault();
        onKnown();
      } else if (event.key === 'ArrowLeft' || event.key === 'Escape') {
        event.preventDefault();
        onUnknown();
      }
    }
  }, [handleFlip, isFlipped, onKnown, onUnknown]);

  const handleTouchStart = useCallback((event: TouchEvent) => {
    if (!isFlipped || !onKnown || !onUnknown || isAnimating) return;

    const touch = event.touches[0];
    setSwipeState({
      isActive: true,
      direction: null,
      distance: 0,
      deltaX: 0,
      deltaY: 0,
      startX: touch.clientX,
      startY: touch.clientY
    });
  }, [isFlipped, onKnown, onUnknown, isAnimating]);

  const handleTouchMove = useCallback((event: TouchEvent) => {
    if (!swipeState.isActive || !isFlipped) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - swipeState.startX;
    const deltaY = touch.clientY - swipeState.startY;

    if (Math.abs(deltaY) > Math.abs(deltaX)) return;

    event.preventDefault();

    const distance = Math.abs(deltaX);
    const direction = deltaX > 0 ? 'right' : 'left';

    setSwipeState(prev => ({
      ...prev,
      direction,
      distance,
      deltaX,
      deltaY
    }));
  }, [swipeState.isActive, swipeState.startX, swipeState.startY, isFlipped]);

  const handleTouchEnd = useCallback((event: TouchEvent) => {
    if (!swipeState.isActive || !isFlipped) {
      setSwipeState({
        isActive: false,
        direction: null,
        distance: 0,
        deltaX: 0,
        deltaY: 0,
        startX: 0,
        startY: 0
      });
      return;
    }

    const swipeThreshold = 100;

    if (swipeState.distance > swipeThreshold) {
      if (swipeState.direction === 'right' && onKnown) {
        onKnown();
      } else if (swipeState.direction === 'left' && onUnknown) {
        onUnknown();
      }
    }

    setSwipeState({
      isActive: false,
      direction: null,
      distance: 0,
      deltaX: 0,
      deltaY: 0,
      startX: 0,
      startY: 0
    });
  }, [swipeState.isActive, swipeState.distance, swipeState.direction, isFlipped, onKnown, onUnknown]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);

    const cardElement = cardRef.current;
    if (cardElement) {
      cardElement.addEventListener('touchstart', handleTouchStart, { passive: true });
      cardElement.addEventListener('touchmove', handleTouchMove, { passive: false });
      cardElement.addEventListener('touchend', handleTouchEnd, { passive: true });
    }

    // Handle window resize for desktop detection
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener('resize', handleResize);
      if (cardElement) {
        cardElement.removeEventListener('touchstart', handleTouchStart);
        cardElement.removeEventListener('touchmove', handleTouchMove);
        cardElement.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [handleKeyPress, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Content renderer with scroll capability
  const renderContent = (content: string, isAnswer: boolean) => (
    <div className="w-full h-full flex flex-col min-w-0">
      <div
        ref={isAnswer ? scrollContainerRef : undefined}
        className={`
          flex-1 flex items-center justify-center min-w-0
          ${contentNeedsScroll ? 'overflow-y-auto flashcard-scroll no-scrollbar' : 'overflow-hidden'}
          px-4 sm:px-6 md:px-8
        `}
      >
        <div className={`${textSizeClass} text-foreground text-center break-words max-w-full w-full min-w-0`}>
          {content}
        </div>
      </div>

      {showScrollHint && contentNeedsScroll && isAnswer && (
        <div className="flex-shrink-0 py-2 text-center">
          <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/70 bg-muted/30 px-3 py-1 rounded-full">
            <svg className="w-3 h-3 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
            <span>Posuň dolů</span>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="w-full overflow-x-hidden">
      <div className="relative w-full mx-auto">
        {/* Card Container - Fixed width to prevent mobile expansion */}
        <div
          ref={cardRef}
          className="relative w-full max-w-full h-[400px] sm:h-[420px] md:h-[380px] lg:h-[400px] cursor-pointer flashcard-active overflow-hidden"
          style={{ perspective: '1000px' }}
          onClick={handleCardClick}
          role="button"
          tabIndex={0}
          aria-label={`Flashcard: ${card.question}. ${isFlipped ? 'Showing answer' : 'Click to reveal answer'}`}
          aria-pressed={isFlipped}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleCardClick();
            }
          }}
        >
          {/* Swipe Visual Feedback Overlay */}
          {swipeState.isActive && swipeState.direction && swipeState.distance > 5 && (
            <div
              className={`
                absolute inset-0 z-10 flex items-center justify-center
                rounded-2xl transition-all duration-100 ease-out pointer-events-none
                ${swipeState.direction === 'right'
                  ? 'bg-green-500/20 border-4 border-green-500/50'
                  : 'bg-red-500/20 border-4 border-red-500/50'
                }
              `}
              style={{
                opacity: Math.min(swipeState.distance / 80, 0.8)
              }}
            >
              <div
                className={`
                  transition-all duration-100 ease-out
                  ${swipeState.direction === 'right' ? 'text-green-500' : 'text-red-500'}
                `}
                style={{
                  transform: `scale(${Math.min(0.5 + (swipeState.distance / 100), 1.5)})`,
                  opacity: Math.min(swipeState.distance / 60, 1)
                }}
              >
                {swipeState.direction === 'right' ? (
                  <Check className="w-16 h-16 sm:w-20 sm:h-20" strokeWidth={3} />
                ) : (
                  <X className="w-16 h-16 sm:w-20 sm:h-20" strokeWidth={3} />
                )}
              </div>
            </div>
          )}

          {/* Flip Container */}
          <div
            className="relative w-full h-full"
            style={{
              transformStyle: 'preserve-3d',
              transform: `
                ${isDesktop
                  ? `rotateX(${isFlipped ? '180deg' : '0deg'})`
                  : `rotateY(${isFlipped ? '180deg' : '0deg'})`
                }
                translateX(${swipeState.isActive && swipeState.deltaX ? `${(isFlipped ? -1 : 1) * swipeState.deltaX * 0.8}px` : '0px'})
                rotateZ(${swipeState.isActive && swipeState.deltaX ? `${(isFlipped ? -1 : 1) * swipeState.deltaX * 0.1}deg` : '0deg'})
                scale(${swipeState.isActive && swipeState.distance > 10 ? Math.max(0.95, 1 - (swipeState.distance * 0.0005)) : '1'})
              `,
              transition: swipeState.isActive ? 'none' : 'transform 0.3s ease-in-out'
            }}
          >
            {/* Front Side (Question) */}
            <div
              className="absolute inset-0 w-full h-full bg-card border-2 border-b-[6px] border-border rounded-2xl shadow-lg overflow-hidden"
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                willChange: 'transform'
              }}
            >
              <div className="w-full h-full min-w-0">
                {renderContent(card.question, false)}
              </div>
            </div>

            {/* Back Side (Answer) */}
            <div
              className="absolute inset-0 w-full h-full bg-orange-50/50 dark:bg-orange-900/10 border-2 border-b-[6px] border-brand-orange dark:border-brand-orange rounded-2xl shadow-lg overflow-hidden"
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: isDesktop ? 'rotateX(180deg)' : 'rotateY(180deg)',
                willChange: 'transform' // Optimize for animation
              }}
            >
              <div className="w-full h-full flex flex-col min-w-0">
                {renderContent(card.answer, true)}

                {card.category && (
                  <div className="flex-shrink-0 text-center py-3 px-2">
                    <span className="inline-block px-3 py-1 bg-muted rounded-full text-xs font-medium text-muted-foreground capitalize whitespace-nowrap">
                      {card.category}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default FlashcardDisplay;
